import { useState, useEffect, useMemo, useRef } from 'react'
import { Trash2, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import type { Node, Edge, SymbolNode, TagNode, ConnectionNode } from '@/service/graph'
// Vision-category mapping is P&ID-only: it exists to stamp the raw `category`
// / `symbol_type` the KG generators classify on. SLD has no emit tier, so those
// stamps are gated on the discipline below rather than applied blindly.
import { resolveVisionCategory, resolveVisionType, visionCategoryToLevelI, INSTRUMENTATION_LEVEL_I } from '@/config/pnidClassification'
import type { PnidLevelI } from '@/config/pnidClassification'
import { useTaxonomy } from './TaxonomyContext'
import { isKnownClassification } from '@/config/classification'
import { linkedTagIdsForSymbol } from './entityDeletion'

interface NodeDetailsPanelV2Props {
  node: Node | undefined
  isOpen: boolean
  onClose: () => void
  updateNode: (node: Node) => Node
  deleteNode: (nodeId: string) => void
  deleteEdgeById?: (edgeId: string) => void
  currentState?: { nodes: Map<string, Node>; edges: Map<string, Edge> }
  /** Snapshot history before Remove deletes the node + its edges, so Undo can restore them. */
  onBeforeRemove?: () => void
}

/** Filter out the literal string "null" that the backend sometimes returns */
function filterNull(val: string | undefined | null): string {
  if (!val || val === 'null') return ''
  return val
}

function getConfidenceBadge(confidence: number | undefined) {
  const pct = confidence != null ? Math.round(confidence * 100 * 10) / 10 : 0
  if (pct >= 90) return { labelKey: 'nodePanel.confidence.high', defaultLabel: 'High', color: 'bg-[#dcfce7] text-[#15803d] border-[#15803d]' }
  if (pct >= 50) return { labelKey: 'nodePanel.confidence.medium', defaultLabel: 'Medium', color: 'bg-[#fefce8] text-[#ca8a04] border-[#ca8a04]' }
  return { labelKey: 'nodePanel.confidence.low', defaultLabel: 'Low', color: 'bg-red-50 text-red-700 border-red-200' }
}

export default function NodeDetailsPanelV2({
  node,
  isOpen,
  onClose,
  updateNode,
  deleteNode,
  deleteEdgeById,
  currentState,
  onBeforeRemove,
}: NodeDetailsPanelV2Props) {
  const { t } = useTranslation()
  const taxonomy = useTaxonomy()

  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [type, setType] = useState('')
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [annotationText, setAnnotationText] = useState('')

  // Latches once Remove deletes this node. A field's onBlur autosave can fire AFTER
  // Remove (the panel unmounts / a focused <select> keeps focus through the click),
  // and updateNode would re-insert the just-deleted node — turning the delete into
  // an update so the tag reappears on save+reload. Reset per node below.
  const removedRef = useRef(false)

  // True when the Tag box is the content editor for this tag (a tag whose text is
  // shown in the box, i.e. NOT an annotation-type tag). Captured at load so a later
  // reclassify doesn't flip it. When true, emptying the Tag box must WIPE content
  // (the user cleared a wrongly-detected tag); when false the box is intentionally
  // blank and content is preserved. Distinguishes a real clear from a blank box.
  const tagBoxEditsContentRef = useRef(false)

  // Sync form state when node changes — matches V1 DetailsFlyout initialization
  useEffect(() => {
    removedRef.current = false
    if (!node) return
    const sym = node.type === 'symbol' ? (node as SymbolNode) : null

    // Classification: prefer saved pnid_level_i/ii/iii, fall back to heuristic
    if (filterNull(node.pnid_level_i)) {
      // Seed the dropdowns from what is SAVED, even when the active taxonomy no
      // longer contains it. The dropdown will show it as an unmatched value
      // rather than blanking — a stranded classification must be visible to the
      // annotator, because the alternative is losing their work silently on the
      // next save. Warn so a taxonomy change that stranded rows is findable.
      //
      // P&ID is deliberately EXCLUDED from the warning. Its taxonomy is stable
      // and plenty of existing nodes carry legacy/heuristic values that were
      // never in the sheet, so warning there is a guaranteed false alarm on
      // real drawings — new log volume with no signal. The risk this exists for
      // is the SLD sheet, which is still being revised.
      if (
        taxonomy.discipline !== 'pnid' &&
        !isKnownClassification(
          taxonomy,
          filterNull(node.pnid_level_i),
          filterNull(node.pnid_level_ii),
          filterNull(node.pnid_level_iii),
        )
      ) {
        console.warn(
          `[classification] node ${node.id} carries ${filterNull(node.pnid_level_i)} ▸ ` +
            `${filterNull(node.pnid_level_ii)} ▸ ${filterNull(node.pnid_level_iii)}, ` +
            `which is not in the ${taxonomy.discipline} taxonomy — it was likely stranded by a taxonomy change`,
        )
      }
      setCategory(filterNull(node.pnid_level_i))
      setSubcategory(filterNull(node.pnid_level_ii))
      setType(filterNull(node.pnid_level_iii))
    } else {
      // Seed from the ACTIVE taxonomy. The P&ID module's guesses are
      // `Equipment` / `Piping ▸ Line`, which do not exist in the SLD sheet: the
      // select would render blank while state held a P&ID value, and
      // `handleFieldBlur` writes `pnid_level_i: category` on ANY field blur —
      // so typing a Note on an unclassified SLD shape silently persisted
      // `Equipment` onto an electrical node.
      const legacyCategory = sym?.category
      const resolved = taxonomy.resolveClassification(
        node.type as 'symbol' | 'tag' | 'connection',
        legacyCategory
      )
      setCategory(resolved.levelI)
      setSubcategory(resolved.levelII)
      setType(resolved.levelIII)
    }

    // A tag node stores its text in `content`, which fills the Tag box, and the
    // box edits `content` (an empty box clears it — ENG-2619). This holds for ALL
    // tags, including annotation-type ones. ENG-2615 previously blanked the Tag
    // box for annotation tags (their text shown only in Annotation Text) — but a
    // Tag-box edit then saved to `content` yet the box re-blanked on reload, so
    // the edit appeared to VANISH. We keep the Tag box as the `content` editor
    // here; the annotation's OCR text still shows in the Annotation Text box.
    tagBoxEditsContentRef.current = node.type === 'tag'
    setLabel(
      filterNull(node.label) ||
      (node.type === 'tag' ? filterNull((node as TagNode).content) : '')
    )
    setNote(filterNull(node.note))
    setAnnotationText(filterNull(node.annotation_text))
  }, [node, taxonomy])

  // Subcategory options: driven by selected category
  // Hooks must run unconditionally — keep these above the early return below
  // so the Rules of Hooks aren't violated when the panel is closed.
  const subcategoryOptions = useMemo(() => {
    if (!category) return []
    return taxonomy.getLevelII(category)
  }, [category, taxonomy])

  // Type options: driven by selected category + subcategory
  const typeOptions = useMemo(() => {
    if (!category || !subcategory) return []
    return taxonomy.getLevelIII(category, subcategory)
  }, [category, subcategory, taxonomy])

  if (!isOpen || !node) return null

  // A hand-drawn object has no model detection, so it has no detection confidence.
  const isManual = node.source === 'manual'

  const confidence = node.confidence
  const pct = confidence != null ? Math.round(confidence * 100 * 10) / 10 : 0
  const badge = getConfidenceBadge(confidence)

  // Category options: PIC001 Level-I values — but a tag/text node is confined to the
  // Annotation branch (a label can't be Equipment/Instrumentation/Piping; those are symbol
  // categories the generator types from the SYMBOL). See categoryOptionsForNodeType (#2).
  const categoryOptions = taxonomy.categoryOptionsForNodeType(node.type)

  // Hard block: Approve stays disabled until the required classification fields are filled
  // (Category always; Subcategory + Type unless Annotation or a no-leaf pair).
  const approveDisabled = taxonomy.isMissingRequiredType(category, subcategory, type)

  const handleApprove = () => {
    // Defensive backstop only — the Approve button is already hard-disabled via `approveDisabled`
    // in the JSX, so this can't normally fire while required fields are missing. Reuse the same
    // flag (don't recompute the predicate) so the two can never diverge.
    if (approveDisabled) return
    // ENG-2622: approval is a human action tracked by `approved`. Do NOT overwrite
    // `confidence` (a model prediction) — keeping it intact means the annotator can
    // still see what the model predicted after approving.
    // Save the displayed classification too: a seeded one is only in local
    // state until a field blurs, and approving must not drop it.
    updateNode({
      ...node,
      pnid_level_i: category || undefined,
      pnid_level_ii: subcategory || undefined,
      pnid_level_iii: type || undefined,
      approved: true,
    })
    toast.success(t('graph_editor:nodePanel.approvedSuccess', { defaultValue: 'Approved Successfully' }))
  }

  const handleRemove = () => {
    // Latch first so any field blur racing this click (or firing as the panel
    // unmounts below) can't resurrect the node via handleFieldBlur's autosave.
    removedRef.current = true
    // Snapshot the full pre-delete state (nodes + edges) before anything is removed,
    // so a single Undo restores the node and its connected edges.
    onBeforeRemove?.()
    // #8 delete-completeness: deleting a SYMBOL also removes its identifier tag node(s)
    // (linked by a `has_label` edge symbol→tag). Otherwise the tag survives as an orphan
    // and the agent still finds the deleted equipment by name. (Datasheet-derived tags are
    // a separate source, not linked from this symbol, so they're correctly left alone.)
    const linkedTagIds =
      currentState ? linkedTagIdsForSymbol(node.id, node.type === 'symbol', currentState.edges) : []
    const nodeIdsToDelete = new Set<string>([node.id, ...linkedTagIds])
    // Delete any edges referencing ANY removed node first, so removal never leaves a
    // dangling edge (parity with the V1 DetailsFlyout remove path).
    if (deleteEdgeById && currentState) {
      const edgesToDelete: string[] = []
      currentState.edges.forEach((edge, edgeId) => {
        if (nodeIdsToDelete.has(edge.fromNodeId) || nodeIdsToDelete.has(edge.toNodeId)) edgesToDelete.push(edgeId)
      })
      edgesToDelete.forEach((edgeId) => deleteEdgeById(edgeId))
    }
    nodeIdsToDelete.forEach((id) => deleteNode(id))
    onClose()
    toast.success(t('graph_editor:nodePanel.nodeRemoved', { defaultValue: 'Node removed' }))
  }

  const handleFieldBlur = () => {
    // Don't let a late blur re-add a node that Remove already deleted.
    if (removedRef.current) return
    const isTag = node.type === 'tag'
    const isSymbol = node.type === 'symbol'
    // ENG-2622: derive is_instrument from the annotator's Category so the canvas
    // colour + instrument fields reflect their selection. When they move a symbol
    // off Instrumentation, clear the now-irrelevant instrument fields too, so a
    // mis-detected instrument's leftover data doesn't linger.
    const isPnid = taxonomy.discipline === 'pnid'
    const nextIsInstrument = isPnid && isSymbol && category === INSTRUMENTATION_LEVEL_I
    // Stamp the raw-vision `category` + `symbol_type` the KG generators classify on
    // (pnid_level_*/is_instrument drive nothing). For a MANUAL add, always stamp. For a
    // DETECTED symbol, stamp ONLY when the human changes its TOP-LEVEL class (picked
    // Level-I ≠ the vision category's implied Level-I) — so we never coarsen a finer
    // vision category (e.g. "pump") into our generic one on a same-class edit.
    const reclassifiedTopLevel =
      isSymbol && !!category && category !== visionCategoryToLevelI((node as SymbolNode).category)
    // SLD never stamps: `resolveVisionCategory` maps the P&ID sheet's Level I
    // values, so an Electrical pick would fall through to its Equipment default
    // and label a breaker as process equipment. Nothing reads these for an SLD.
    const stampVision = isPnid && isSymbol && !!category && (node.source === 'manual' || reclassifiedTopLevel)
    const nextCategory = stampVision ? resolveVisionCategory(category as PnidLevelI, subcategory) : null
    // symbol_type (→ symbols[].type → db_ask dexpi_class). On reclassify the OLD fine
    // subtype is stale and would CONTRADICT the new category (e.g. moving a "pressure
    // gauge" instrument to Equipment must not leave type="PRESSURE GAUGE", or db_ask still
    // reads it as an instrument). Use the fine leaf token when one exists (valves), else
    // fall back to the coarse category token so type agrees with category (#2).
    const nextType = stampVision ? (resolveVisionType(type) ?? nextCategory) : null
    // For an annotation-type tag the Tag box is intentionally empty (its text
    // shows in Annotation Text), so DON'T write the empty Tag input back to
    // `content` — that would wipe the tag's OCR text and drop it from the
    // labelled-JSON export. Preserve the node's existing content instead.
    updateNode({
      ...node,
      pnid_level_i: category || undefined,
      pnid_level_ii: subcategory || undefined,
      pnid_level_iii: type || undefined,
      // ENG-2595/ENG-2589: a Tag node's identifier text is persisted as `content` (the only
      // field the tag write/read path round-trips). The Label input was being written to
      // `label`, which the tag persistence layer ignores — so the edit was silently dropped
      // on save+reload. Route it to `content` for tags (matching what add-node already does),
      // and clear the dead `label` so the panel reads back the corrected text.
      label: isTag ? undefined : (label || undefined),
      note: note || undefined,
      annotation_text: annotationText || undefined,
      // For a tag, `content` follows the Tag box. When the box IS the content editor
      // (a normal tag whose text is shown there), write the box verbatim — so emptying
      // it CLEARS content, letting the user delete wrongly-detected tag text. When the
      // box is intentionally blank (an annotation-type tag, or one reclassified from it
      // without retyping), fall back to the existing content so an empty box doesn't
      // wipe the OCR text that lives in Annotation Text / the labelled-JSON export.
      ...(isTag
        ? { content: tagBoxEditsContentRef.current ? label : (label || ((node as TagNode).content ?? '')) }
        : {}),
      ...(isSymbol ? { is_instrument: nextIsInstrument } : {}),
      // When reclassifying (stampVision), write category/symbol_type UNCONDITIONALLY —
      // incl. '' when the new class has no vision category (Annotation / Piping-non-Valve),
      // so the stale raw-vision value is CLEARED rather than left to contradict the new
      // pnid_level (a detected instrument reclassified as Annotation must not keep
      // category='instrument'). The store persists the '' clear (audit #2).
      ...(stampVision ? { category: nextCategory ?? '' } : {}),
      ...(stampVision ? { symbol_type: nextType ?? '' } : {}),
    })
  }

  const inputClass = 'w-full h-9 px-3 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground'

  // Required-field validation. A required field that's empty gets a red outline. This mirrors
  // the Approve gate (isMissingRequiredType) exactly, so the reddened fields are precisely what
  // keeps Approve disabled: Category is always required; Subcategory + Type are required EXCEPT
  // for Annotation (Category only), and Type is required only when one is actually selectable.
  const subcategoryRequired = category !== taxonomy.annotationLevelI
  const typeRequired = subcategoryRequired && typeOptions.length > 0
  const subcategoryMissing = subcategoryRequired && !!category && !subcategory
  const typeMissing = typeRequired && !type
  const fieldClass = (missing: boolean) =>
    `w-full h-9 px-3 rounded-md border bg-white dark:bg-dark-card text-sm text-foreground ${missing ? 'border-red-500 ring-1 ring-red-500' : 'border-[#e5e5e5] dark:border-dark-border'}`

  return (
    <div className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
      {/* Header — the X closes the panel and deselects the node. */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <h3 className="text-base font-semibold text-foreground">
          {t('graph_editor:nodePanel.nodeDetails', { defaultValue: 'Node Details' })}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('graph_editor:nodePanel.close', { defaultValue: 'Close' })}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <div className="flex flex-col gap-5">
          {/* Category */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.category', { defaultValue: 'Category' })}<span className="text-red-500"> *</span></label>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setSubcategory(''); setType('') }}
              onBlur={handleFieldBlur}
              className={fieldClass(false)}
            >
              <option value="">{t('graph_editor:nodePanel.selectCategory', { defaultValue: 'Select category' })}</option>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
              {category && !(categoryOptions as readonly string[]).includes(category) && <option value={category}>{category}</option>}
            </select>
            {node.type === 'tag' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {t('graph_editor:nodePanel.tagClassifyHint', {
                  defaultValue: 'This is a tag label. To reclassify the equipment, select its symbol instead.',
                })}
              </p>
            )}
          </div>

          {/* Subcategory */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.subcategory', { defaultValue: 'Subcategory' })}{subcategoryRequired && <span className="text-red-500"> *</span>}</label>
            <select
              value={subcategory}
              onChange={(e) => { setSubcategory(e.target.value); setType('') }}
              onBlur={handleFieldBlur}
              disabled={!category}
              className={fieldClass(subcategoryMissing)}
            >
              <option value="">{t('graph_editor:nodePanel.selectSubcategory', { defaultValue: 'Select subcategory' })}</option>
              {subcategoryOptions.map(s => <option key={s} value={s}>{s}</option>)}
              {subcategory && !subcategoryOptions.includes(subcategory) && <option value={subcategory}>{subcategory}</option>}
            </select>
          </div>

          {/* Type */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.type', { defaultValue: 'Type' })}{typeRequired && <span className="text-red-500"> *</span>}</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              onBlur={handleFieldBlur}
              disabled={!subcategory}
              className={fieldClass(typeMissing)}
            >
              <option value="">{t('graph_editor:nodePanel.selectType', { defaultValue: 'Select type' })}</option>
              {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              {type && !typeOptions.includes(type) && <option value={type}>{type}</option>}
              {typeOptions.length === 0 && subcategory && <option value="" disabled>{t('graph_editor:nodePanel.notApplicable', { defaultValue: 'N/A' })}</option>}
            </select>
          </div>

          {/* Tag */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.label', { defaultValue: 'Tag' })}</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={handleFieldBlur}
              className={inputClass}
            />
          </div>

          {/* Annotation Text — shown for every category, directly below Tag */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.annotationText', { defaultValue: 'Annotation Text' })}</label>
            <input
              type="text"
              value={annotationText}
              onChange={(e) => setAnnotationText(e.target.value)}
              onBlur={handleFieldBlur}
              className={inputClass}
            />
          </div>


          {/* Note */}
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">
              {t('graph_editor:nodePanel.note', { defaultValue: 'Note' })} <span className="font-normal text-muted-foreground">{t('graph_editor:nodePanel.optional', { defaultValue: '(Optional)' })}</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={handleFieldBlur}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground resize-none"
            />
          </div>

          {/* Line Number — read-only: the pipe line number the parse gave this segment (VISN-140).
              The same number the KG reads for the pipe, so a click shows which line it is on. */}
          {node?.type === 'connection' && (node as ConnectionNode).lineNumber && (
            <div>
              <label className="text-sm font-semibold text-foreground mb-1.5 block">
                {t('graph_editor:nodePanel.lineNumber', { defaultValue: 'Line Number' })}
              </label>
              <input type="text" value={(node as ConnectionNode).lineNumber} readOnly className={`${inputClass} font-mono`} />
            </div>
          )}

          {/* Confidence Score — a model-detection signal, so hidden for hand-drawn
              (manual) objects which have no detection confidence (ENG-2622). */}
          {!isManual && (
          <div>
            <label className="text-sm font-semibold text-foreground mb-2 block">{t('graph_editor:nodePanel.confidenceScore', { defaultValue: 'Confidence Score' })}</label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">{t('graph_editor:nodePanel.detectionConfidence', { defaultValue: 'Detection Confidence' })}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#0e7490]">{pct}%</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${badge.color}`}>
                  {t(`graph_editor:${badge.labelKey}`, { defaultValue: badge.defaultLabel })}
                </span>
              </div>
            </div>
            {/* VISN-291: the composed review-trust score the Review layer flags by.
                Shown so a flagged node explains itself (raw confidence can be high
                while trust is low, e.g. a near-tie with another class). */}
            {node.trust != null && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-foreground">{t('graph_editor:nodePanel.reviewTrust', { defaultValue: 'Review Trust' })}</span>
                <span className="text-sm font-medium text-[#0e7490]">{Math.round(node.trust * 100)}%</span>
              </div>
            )}
            {node.trust != null && node.siglip_second_type && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">{t('graph_editor:nodePanel.possibleAlternative', { defaultValue: 'Possible alternative' })}</span>
                <span className="text-xs font-medium text-muted-foreground">{node.siglip_second_type}</span>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Bottom actions */}
      <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-[#e5e5e5] dark:border-dark-border shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
          onClick={handleRemove}
        >
          <Trash2 className="w-4 h-4" />
          {t('graph_editor:nodePanel.remove', { defaultValue: 'Remove' })}
        </Button>
        <Button
          size="sm"
          className="gap-1"
          disabled={approveDisabled}
          onClick={handleApprove}
          title={approveDisabled
            ? String(t('graph_editor:nodePanel.approveBlockedMissingType', { defaultValue: 'Fill the required fields (Category, Subcategory, Type) before approving' }))
            : undefined}
        >
          <Check className="w-4 h-4" />
          {t('graph_editor:nodePanel.approve', { defaultValue: 'Approve' })}
        </Button>
      </div>
    </div>
  )
}
